// Renders a shareable neo-brutalist vocab card to a PNG blob — fully client-side,
// dynamic per capture. Used by LanguageLoop to turn every word into a social post.

const PASTEL_HEX = {
  "bg-mint": "#A7F3D0",
  "bg-lavender": "#E9D5FF",
  "bg-butter": "#FDE68A",
  "bg-sky": "#BAE6FD",
  "bg-peach": "#FFDAB9",
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrap(ctx, text, maxWidth) {
  const words = (text || "").split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

export async function generateShareCard(result, pastelClass = "bg-mint") {
  try { await document.fonts.ready; } catch { /* fonts optional */ }

  const S = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext("2d");

  const bg = PASTEL_HEX[pastelClass] || PASTEL_HEX["bg-mint"];
  ctx.fillStyle = "#FAFAFA";
  ctx.fillRect(0, 0, S, S);

  // Outer pastel panel with hard shadow + black border
  const pad = 70, panelW = S - pad * 2, panelH = S - pad * 2;
  ctx.fillStyle = "#0A0A0A";
  roundRect(ctx, pad + 16, pad + 16, panelW, panelH, 40);
  ctx.fill();
  ctx.fillStyle = bg;
  roundRect(ctx, pad, pad, panelW, panelH, 40);
  ctx.fill();
  ctx.lineWidth = 6;
  ctx.strokeStyle = "#0A0A0A";
  roundRect(ctx, pad, pad, panelW, panelH, 40);
  ctx.stroke();

  const cx = pad + 60;
  let y = pad + 110;

  // Wordmark
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "800 56px 'Cabinet Grotesk', sans-serif";
  ctx.fillText("Fluent", cx, y);
  ctx.fillStyle = "#FF6B6B";
  ctx.fillText("Loop", cx + ctx.measureText("Fluent").width, y);

  // Label pill
  y += 70;
  ctx.font = "700 26px 'Satoshi', sans-serif";
  const label = "I LEARNED TODAY";
  const lw = ctx.measureText(label).width + 50;
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, cx, y - 36, lw, 52, 26);
  ctx.fill();
  ctx.lineWidth = 4; ctx.strokeStyle = "#0A0A0A";
  roundRect(ctx, cx, y - 36, lw, 52, 26);
  ctx.stroke();
  ctx.fillStyle = "#0A0A0A";
  ctx.fillText(label, cx + 25, y);

  // The word (big)
  y += 150;
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "800 130px 'Cabinet Grotesk', sans-serif";
  let word = result.word || "";
  while (ctx.measureText(word).width > panelW - 120 && parseInt(ctx.font) > 60) {
    const size = parseInt(ctx.font) - 8;
    ctx.font = `800 ${size}px 'Cabinet Grotesk', sans-serif`;
  }
  ctx.fillText(word, cx, y);

  // Translation + pronunciation
  y += 70;
  ctx.font = "700 46px 'Satoshi', sans-serif";
  ctx.fillStyle = "#374151";
  ctx.fillText(result.translation || "", cx, y);
  y += 50;
  ctx.font = "500 32px 'JetBrains Mono', monospace";
  ctx.fillStyle = "#6B7280";
  ctx.fillText(`/${result.pronunciation || ""}/`, cx, y);

  // Sentence box
  y += 50;
  const boxH = 200, boxW = panelW - 120;
  ctx.fillStyle = "rgba(255,255,255,0.75)";
  roundRect(ctx, cx, y, boxW, boxH, 28);
  ctx.fill();
  ctx.lineWidth = 4; ctx.strokeStyle = "#0A0A0A";
  roundRect(ctx, cx, y, boxW, boxH, 28);
  ctx.stroke();

  ctx.fillStyle = "#0A0A0A";
  ctx.font = "700 40px 'Cabinet Grotesk', sans-serif";
  let ty = y + 60;
  for (const line of wrap(ctx, result.sentence, boxW - 70).slice(0, 2)) {
    ctx.fillText(line, cx + 35, ty);
    ty += 50;
  }
  ctx.font = "400 28px 'Satoshi', sans-serif";
  ctx.fillStyle = "#6B7280";
  for (const line of wrap(ctx, result.sentence_translation, boxW - 70).slice(0, 1)) {
    ctx.fillText(line, cx + 35, ty + 8);
  }

  // Footer tagline
  ctx.font = "700 28px 'Satoshi', sans-serif";
  ctx.fillStyle = "#0A0A0A";
  ctx.fillText("Learn any word with your camera · getfluentloop.com", cx, pad + panelH - 45);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.92));
}

export async function generateRecapCard(recap, targetLangName = "") {
  try { await document.fonts.ready; } catch { /* fonts optional */ }
  const S = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = S; canvas.height = S;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#FAFAFA"; ctx.fillRect(0, 0, S, S);
  const pad = 70, panelW = S - pad * 2, panelH = S - pad * 2;
  ctx.fillStyle = "#0A0A0A"; roundRect(ctx, pad + 16, pad + 16, panelW, panelH, 40); ctx.fill();
  ctx.fillStyle = "#FDE68A"; roundRect(ctx, pad, pad, panelW, panelH, 40); ctx.fill();
  ctx.lineWidth = 6; ctx.strokeStyle = "#0A0A0A"; roundRect(ctx, pad, pad, panelW, panelH, 40); ctx.stroke();

  const cx = pad + 60;
  let y = pad + 110;
  ctx.fillStyle = "#0A0A0A"; ctx.font = "800 52px 'Cabinet Grotesk', sans-serif";
  ctx.fillText("Fluent", cx, y);
  ctx.fillStyle = "#FF6B6B"; ctx.fillText("Loop", cx + ctx.measureText("Fluent").width, y);

  y += 66;
  ctx.font = "700 26px 'Satoshi', sans-serif";
  const label = `MY WEEK IN ${(targetLangName || "").toUpperCase()}`.trim();
  const lw = ctx.measureText(label).width + 50;
  ctx.fillStyle = "#FFFFFF"; roundRect(ctx, cx, y - 36, lw, 52, 26); ctx.fill();
  ctx.lineWidth = 4; ctx.strokeStyle = "#0A0A0A"; roundRect(ctx, cx, y - 36, lw, 52, 26); ctx.stroke();
  ctx.fillStyle = "#0A0A0A"; ctx.fillText(label, cx + 25, y);

  y += 150;
  ctx.font = "800 150px 'Cabinet Grotesk', sans-serif";
  ctx.fillText(String(recap.words_learned ?? 0), cx, y);
  ctx.font = "700 40px 'Satoshi', sans-serif"; ctx.fillStyle = "#374151";
  ctx.fillText("new words learned", cx, y + 52);

  // stat tiles
  y += 130;
  const tiles = [
    ["🔥", `${recap.streak_count ?? 0} streak`],
    ["📅", `${recap.active_days ?? 0}/7 days`],
    ["📸", `${recap.captures ?? 0} captures`],
    ["💬", `${recap.conversations ?? 0} chats`],
  ];
  const tw = (panelW - 120 - 30) / 2, th = 90, gap = 30;
  ctx.textBaseline = "middle";
  tiles.forEach((t, i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const x = cx + col * (tw + gap), ty = y + row * (th + gap);
    ctx.fillStyle = "#FFFFFF"; roundRect(ctx, x, ty, tw, th, 20); ctx.fill();
    ctx.lineWidth = 4; ctx.strokeStyle = "#0A0A0A"; roundRect(ctx, x, ty, tw, th, 20); ctx.stroke();
    ctx.fillStyle = "#0A0A0A"; ctx.font = "700 40px 'Satoshi', sans-serif";
    ctx.fillText(`${t[0]}  ${t[1]}`, x + 24, ty + th / 2);
  });
  ctx.textBaseline = "alphabetic";

  ctx.font = "700 28px 'Satoshi', sans-serif"; ctx.fillStyle = "#0A0A0A";
  ctx.fillText("Learn a language with your camera · getfluentloop.com", cx, pad + panelH - 45);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.92));
}
